import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Box, Flex, Text } from 'rebass/styled-components';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Card } from 'components/atoms/Card';
import { Dropdown } from 'components/molecules/dropdown';
import { useTreeItemRow } from './hooks/useTreeItemRow';

export function TreeItemRow({
  leftIcon: LeftIcon,
  indentPx,
  label,
  href,
  onRowClick,
  actions,
  instruction,
  createActions,
  createTriggerIcon: CreateTriggerIcon,
  isActive,
}) {
  const { actionsRef, createRef, handleRowClick, buildActionClick, buildCreateActionClick } = useTreeItemRow({
    onRowClick,
  });

  return (
    <Box sx={{ position: 'relative' }}>
      <Flex
        tx="mainMenu"
        variant="item"
        alignItems="center"
        ml={`${indentPx}px`}
        sx={{
          cursor: 'pointer',
          gap: '8px',
          borderRadius: '4px',
          fontSize: '14px',
          fontWeight: '500',
          minHeight: '26px',
          position: 'relative',
          transition: 'all 0.3s ease',
          bg: isActive ? 'neutral.50' : 'transparent',
          color: isActive ? 'text.dark' : 'text.main',
          ':hover': { bg: 'neutral.50', color: 'text.dark' },
          ':hover .tree-item-row-actions': { opacity: 1 },
        }}
        onClick={handleRowClick}
      >
        {instruction && !instruction.blocked ? null : null}
        {LeftIcon ? <Box as={LeftIcon} width="16px" height="16px" aria-hidden="true" flex="0 0 auto" /> : null}
        {href ? (
          <Link
            to={href}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            style={{ textDecoration: 'none', color: 'inherit', flex: 1, minWidth: 0 }}
          >
            <Text sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
              {label}
            </Text>
          </Link>
        ) : (
          <Text sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
            {label}
          </Text>
        )}
        <Flex
          className="tree-item-row-actions"
          sx={{
            gap: 0,
            alignItems: 'center',
            pl: '4px',
            position: 'absolute',
            right: 0,
            top: '4px',
            opacity: 0,
            transition: 'all .3s ease',
          }}
        >
          <Dropdown
            ref={actionsRef}
            placement="right-start"
            trigger={
              <Flex
                data-tree-actions="true"
                sx={{
                  width: '24px',
                  height: '24px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bg: 'neutral.50',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  svg: { color: 'neutral.400', pointerEvents: 'none' },
                  transition: 'all .3s ease',
                  ':hover': {
                    bg: 'neutral.100',
                    svg: { color: 'neutral.900' },
                  },
                }}
              >
                <Box as={MoreHorizontal} size="16px" />
              </Flex>
            }
          >
            <Card sx={{ cursor: 'pointer', width: '160px', gap: '4px' }} onClick={(event) => event.stopPropagation()}>
              {actions.map((action) => {
                if (action.divider) {
                  return <Box key={`divider-${action.text}`} as="hr" variant="hr" />;
                }
                const baseColor = action.sx && action.sx.color ? action.sx.color : 'text.main';
                const hoverColor = baseColor === 'text.main' ? 'text.dark' : baseColor;
                return (
                  <Flex
                    key={`action-${action.text}`}
                    onClick={buildActionClick(action)}
                    alignItems="center"
                    sx={{
                      gap: '8px',
                      color: baseColor,
                      py: '4px',
                      ...(action.sx || {}),
                      '&:hover': { color: hoverColor },
                    }}
                  >
                    {action.icon ? (
                      <Box as={action.icon} size="16px" sx={{ color: 'inherit', ...(action.iconSx || {}) }} />
                    ) : null}
                    <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                      {action.text}
                    </Text>
                  </Flex>
                );
              })}
            </Card>
          </Dropdown>

          {createActions && createActions.length > 0 && (
            <Dropdown
              ref={createRef}
              placement="right-start"
              trigger={
                <Flex
                  data-tree-create="true"
                  sx={{
                    width: '24px',
                    height: '24px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bg: 'neutral.50',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    svg: { color: 'neutral.400', pointerEvents: 'none' },
                    transition: 'all .3s ease',
                    ':hover': {
                      bg: 'neutral.100',
                      svg: { color: 'neutral.900' },
                    },
                  }}
                >
                  <Box as={CreateTriggerIcon || Plus} size="16px" />
                </Flex>
              }
            >
              <Card sx={{ cursor: 'pointer', width: '160px', gap: '4px' }} onClick={(event) => event.stopPropagation()}>
                {createActions.map((action) => (
                  <Flex
                    key={`create-${action.text}`}
                    onClick={buildCreateActionClick(action)}
                    alignItems="center"
                    sx={{ gap: '8px', color: 'text.main', py: '4px', '&:hover': { color: 'text.dark' } }}
                  >
                    {action.icon ? <Box as={action.icon} size="16px" /> : null}
                    <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                      {action.text}
                    </Text>
                  </Flex>
                ))}
              </Card>
            </Dropdown>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

TreeItemRow.propTypes = {
  leftIcon: PropTypes.any,
  indentPx: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  onRowClick: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string, onClick: PropTypes.func, icon: PropTypes.any }))
    .isRequired,
  instruction: PropTypes.object,
  createActions: PropTypes.arrayOf(
    PropTypes.shape({ text: PropTypes.string, onClick: PropTypes.func, icon: PropTypes.any }),
  ),
  createTriggerIcon: PropTypes.any,
  isActive: PropTypes.bool,
};

TreeItemRow.defaultProps = {
  leftIcon: undefined,
  href: undefined,
  onRowClick: undefined,
  instruction: undefined,
  createActions: undefined,
  createTriggerIcon: undefined,
  isActive: false,
};

export default TreeItemRow;
